import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {CloseOutline} from 'antd-mobile-icons';
import {ImageViewer, Picker, SpinLoading, Toast} from 'antd-mobile';
import {gql, useLazyQuery, useMutation, useQuery} from '@apollo/client';
import i18n from '../../../i18n';
import {useTranslation} from 'react-i18next';
import {showError} from '../../../utils/exceptionUtils';
import {formatTimestampToSecondsTime} from '../../../utils/dateTimeUtils';
import {GET_NEXT_ACTION_LIST, NextActionListResponse} from '../../../graphql/queries/getNextActionList';
import {
    ExceptionDictionaryByCodeResponse,
    GET_EXCEPTION_DICTIONARY_BY_REASON_CODE,
    GET_EXCEPTION_DICTIONARY_BY_WAREHOUSE_TYPE,
    ExceptionDictionaryListResponse
} from '../../../graphql/queries/exceptionQueries';
import { WarehouseSiteType } from '../../../enums/WarehouseTypeEnum';
import MultiPhotoGalleryView from './MultiPhotoGalleryView';
import { pdaOpenCamera } from '../../../utils/appRuntime';
import { fileUpload } from '../../../utils/storageUtils';
import { base64ToFile } from '../../../services/DeliveryDataService';

// GraphQL Mutation for setting next action
const HANDLE_SET_NEXT_ACTION_SCAN_EVENT = gql`
    mutation HandleSetNextActionScanEvent($barCode: String!, $actionName: String!) {
        handleSetNextActionScanEvent(barCode: $barCode, actionName: $actionName)
    }
`;

// GraphQL Mutation for exception scan
const HANDLE_EXCEPTION_SCAN_EVENT = gql`
    mutation HandleExceptionScanEvent($trackingNumber: String!, $warehouseSiteType: WarehouseSiteType!, $exceptionReasonCode: String!, $exceptionImageS3KeysJson: [String!]) {
        handleExceptionScanEvent(trackingNumber: $trackingNumber, warehouseSiteType: $warehouseSiteType, exceptionReasonCode: $exceptionReasonCode, exceptionImageS3KeysJson: $exceptionImageS3KeysJson)
    }
`;

// GraphQL Mutation for return to WHS scan
const HANDLE_RETURN_TO_WHS_SCAN_EVENT = gql`
    mutation HandleReturnToWhsScanEvent($barCode: String!, $isOperate: Boolean) {
        result: handleReturnToWhsScanEvent(barCode: $barCode, isOperate: $isOperate) {
            success
            nextAction
            overallStatus
            exceptionReasonCode
            deliveryAttempts
            sortingCode
            isAbnormal
            exceptionImageS3KeysJson
            exceptionImageS3UrlsJson
            taskDate
            routeName
            stopNumberStr
            driverAccountName
            dapName
            destWarehouseArrivedAt
            isPickupArrivalScan
            isDestWarehouseArrived
        }
    }
`;

// GraphQL Mutation for WHS arrival scan
const HANDLE_RTW_WHS_ARRIVAL_SCAN_EVENT = gql`
    mutation HandleRTWWhsArrivalScanEvent($barCode: String!) {
        handleRTWWhsArrivalScanEvent(barCode: $barCode)
    }
`;

interface OrderDetailCardProps {
    trackingNumber: string;
    overallStatus: string;
    exceptionReasonCode?: string;
    deliveryAttempts?: number;
    sortingCode?: string;
    nextAction?: string;
    isAbnormal?: boolean;
    exceptionImageS3Keys?: string[];
    exceptionImageS3UrlsJson?: string[];
    taskDate?: string;
    routeName?: string;
    stopNumberStr?: string;
    driverAccountName?: string;
    dapName?: string;
    destWarehouseArrivedAt?: number;
    isPickupArrivalScan?: boolean;
    isDestWarehouseArrived?: boolean;
    justReturnedToWhs?: boolean;
    onClose: () => void;
    onNextActionChanged?: (nextAction: string, shouldClose?: boolean) => void; // 回调函数，用于通知父组件刷新数据
    onNewScanSuccess?: (scanData: {
        trackingNumber: string;
        nextAction?: string;
        overallStatus?: string;
        exceptionReasonCode?: string;
        isAbnormal?: boolean;
    }) => void; // 回调函数，用于通知父组件添加新的成功扫描记录
}

export interface OrderDetailCardRef {
    close: () => void;
}

export const OrderDetailCard = forwardRef<OrderDetailCardRef, OrderDetailCardProps>(({
    trackingNumber,
    overallStatus,
    exceptionReasonCode,
    deliveryAttempts,
    sortingCode,
    nextAction,
    isAbnormal,
    exceptionImageS3Keys,
    exceptionImageS3UrlsJson,
    taskDate,
    routeName,
    stopNumberStr,
    driverAccountName,
    dapName,
    destWarehouseArrivedAt,
    isPickupArrivalScan,
    isDestWarehouseArrived,
    justReturnedToWhs,
    onClose,
    onNextActionChanged,
    onNewScanSuccess
}, ref) => {
    const { t } = useTranslation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [imageViewerVisible, setImageViewerVisible] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
    const [selectedAction, setSelectedAction] = useState<string | null>(nextAction || null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pickerVisible, setPickerVisible] = useState(false);
    const [showExceptionReasonPicker, setShowExceptionReasonPicker] = useState(false);
    const [showExceptionReasonDropdown, setShowExceptionReasonDropdown] = useState(false);
    const [selectedExceptionReason, setSelectedExceptionReason] = useState<string>('');
    const [currentOverallStatus, setCurrentOverallStatus] = useState<string>(overallStatus);
    const [currentIsAbnormal, setCurrentIsAbnormal] = useState<boolean>(isAbnormal || false);
    const [justReturnedToWhsState, setJustReturnedToWhs] = useState<boolean>(justReturnedToWhs || false); // 标识是否刚刚执行了返仓操作
    const [currentExceptionReasonCode, setCurrentExceptionReasonCode] = useState<string | undefined>(exceptionReasonCode); // 当前异常原因代码
    
    // 照片相关状态
    const [photosArray, setPhotosArray] = useState<string[]>([]);
    const [currentExceptionImageS3UrlsJson, setCurrentExceptionImageS3UrlsJson] = useState<string[]>(exceptionImageS3UrlsJson || []);
    
    // 照片配置常量
    const kMaxSelectorPhotoLength = 2;
    const kMinSelectorPhotoLength = 1;
    // 异常字典查询
    const [getExceptionDictionary, { data: exceptionDictData }] = useLazyQuery<ExceptionDictionaryByCodeResponse>(GET_EXCEPTION_DICTIONARY_BY_REASON_CODE);
    
    // WHS异常字典查询
    const { data: queryData, loading } = useQuery<ExceptionDictionaryListResponse>(
        GET_EXCEPTION_DICTIONARY_BY_WAREHOUSE_TYPE,
        {
            variables: {
                warehouseSiteType: WarehouseSiteType.WAREHOUSE
            }
        }
    );
    
    // 获取当前语言对应的异常描述
    const getExceptionLabel = (exception: any): string => {
        const currentLang = i18n.language;
        switch (currentLang) {
            case 'zh-CN':
            case 'zh':
                return exception.secondaryTypeZh;
            case 'es':
                return exception.secondaryTypeEs || exception.secondaryTypeEn;
            default:
                return exception.secondaryTypeEn;
        }
    };

    // 转换异常字典为 Picker 选项
    const exceptionOptions = queryData?.getExceptionsDictionaryByWarehouseType
        ?.map(exception => ({
            label: getExceptionLabel(exception),
            value: exception.exceptionReasonCode
        })) || [];

    // 获取异常原因显示文本
    const getExceptionReasonLabel = (reasonCode: string) => {
        if (!queryData?.getExceptionsDictionaryByWarehouseType) {
            return reasonCode;
        }

        const exception = queryData.getExceptionsDictionaryByWarehouseType.find(item => item.exceptionReasonCode === reasonCode);
        if (!exception) return reasonCode;

        return getExceptionLabel(exception);
    };

    // 获取当前选中的异常类型
    const getSelectedExceptionType = () => {
        if (!queryData?.getExceptionsDictionaryByWarehouseType || !selectedExceptionReason) {
            return undefined;
        }
        return queryData.getExceptionsDictionaryByWarehouseType.find(
            exception => exception.exceptionReasonCode === selectedExceptionReason
        );
    };

    // 判断当前选中的异常类型是否需要上传图片
    const shouldShowPhotoGallery = (): boolean => {
        const selectedType = getSelectedExceptionType();
        return selectedType?.isUploadsImages || false;
    };
    
    // GraphQL mutations
    const [handleExceptionScanEvent] = useMutation(HANDLE_EXCEPTION_SCAN_EVENT);
    const [handleReturnToWhsScanEvent] = useMutation(HANDLE_RETURN_TO_WHS_SCAN_EVENT);
    const [handleRTWWhsArrivalScanEvent] = useMutation(HANDLE_RTW_WHS_ARRIVAL_SCAN_EVENT);
    
    // 设置默认异常原因为 ERWHS008，如果没有则选择第一个选项
    useEffect(() => {
        if (queryData?.getExceptionsDictionaryByWarehouseType && !selectedExceptionReason) {
            // 优先查找 ERWHS008
            const erwhs008Exception = queryData.getExceptionsDictionaryByWarehouseType.find(
                exception => exception.exceptionReasonCode === 'ERWHS008'
            );
            
            if (erwhs008Exception) {
                setSelectedExceptionReason(erwhs008Exception.exceptionReasonCode);
            } else {
                // 如果没有找到 ERWHS008，则选择第一个选项
                const firstException = queryData.getExceptionsDictionaryByWarehouseType[0];
                if (firstException) {
                    setSelectedExceptionReason(firstException.exceptionReasonCode);
                }
            }
        }
    }, [queryData, selectedExceptionReason]);

    // 同步nextAction变化（仅当不是trackingNumber变化时）
    useEffect(() => {
        setSelectedAction(nextAction || null);
    }, [nextAction]);

    // 重置状态当trackingNumber变化时
    useEffect(() => {
        // 重置滚动位置到顶部
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        
        setImageUrls([]);
        setImagesLoaded(false);
        setIsLoadingImages(false);
        setImageViewerVisible(false);
        setCurrentImageUrl('');
        setJustReturnedToWhs(justReturnedToWhs || false);
        setSelectedExceptionReason(''); // 重置选中的异常原因
        setIsSubmitting(false); // 重置提交状态
        setPickerVisible(false); // 重置选择器状态
        setShowExceptionReasonPicker(false); // 重置异常原因选择器状态
        setShowExceptionReasonDropdown(false); // 重置异常原因下拉框状态
        setCurrentOverallStatus(overallStatus); // 重置当前状态为新的props值
        setCurrentIsAbnormal(isAbnormal || false); // 重置当前异常状态为新的props值
        setSelectedAction(nextAction || null); // 重置选中的动作为新的props值
        setCurrentExceptionReasonCode(exceptionReasonCode); // 重置异常原因代码为新的props值
        setPhotosArray([]); // 重置照片数组
        setCurrentExceptionImageS3UrlsJson(exceptionImageS3UrlsJson || []); // 重置异常图片URLs
        
        // 立即设置默认异常原因，避免竞争条件
        if (queryData?.getExceptionsDictionaryByWarehouseType) {
            // 优先查找 ERWHS008
            const erwhs008Exception = queryData.getExceptionsDictionaryByWarehouseType.find(
                exception => exception.exceptionReasonCode === 'ERWHS008'
            );
            
            if (erwhs008Exception) {
                setSelectedExceptionReason(erwhs008Exception.exceptionReasonCode);
            } else {
                // 如果没有找到 ERWHS008，则选择第一个选项
                const firstException = queryData.getExceptionsDictionaryByWarehouseType[0];
                if (firstException) {
                    setSelectedExceptionReason(firstException.exceptionReasonCode);
                }
            }
        }
    }, [trackingNumber, overallStatus, isAbnormal, nextAction, justReturnedToWhs, exceptionImageS3UrlsJson, queryData]);

    // 同步overallStatus变化（仅当不是trackingNumber变化时）
    useEffect(() => {
        setCurrentOverallStatus(overallStatus);
        if (overallStatus !== 'ARRIVED_WHS') {
            setJustReturnedToWhs(false);
        }
    }, [overallStatus]);

    // 同步isAbnormal变化（仅当不是trackingNumber变化时）
    useEffect(() => {
        setCurrentIsAbnormal(isAbnormal || false);
    }, [isAbnormal]);

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
        close: onClose
    }));

    // 查询异常字典 - 使用当前的异常原因代码
    useEffect(() => {
        const reasonCodeToQuery = currentExceptionReasonCode || exceptionReasonCode;
        if (reasonCodeToQuery) {
            getExceptionDictionary({ variables: { exceptionReasonCode: reasonCodeToQuery } });
        }
    }, [currentExceptionReasonCode, exceptionReasonCode, getExceptionDictionary]);

    // 获取国际化的状态显示文本
    const getLocalizedStatus = (status: string) => {
        return t(`enum.overallStatus.${status}`, status);
    };

    // 获取国际化的异常原因显示文本
    const getLocalizedExceptionReason = (reasonCode?: string) => {
        if (!reasonCode || !exceptionDictData?.getExceptionDictionaryByReasonCode) {
            return reasonCode || '-';
        }

        const exceptionDict = exceptionDictData.getExceptionDictionaryByReasonCode;
        const currentLang = i18n.language || 'en';

        // 根据语言选择对应字段
        if (currentLang.startsWith('zh')) {
            return exceptionDict.secondaryTypeZh || exceptionDict.secondaryTypeEn || reasonCode;
        } else if (currentLang.startsWith('es')) {
            return exceptionDict.secondaryTypeEs || exceptionDict.secondaryTypeEn || reasonCode;
        } else {
            // 默认英文
            return exceptionDict.secondaryTypeEn || exceptionDict.secondaryTypeZh || reasonCode;
        }
    };

    // 根据NextAction获取对应的颜色样式
    const getActionColorStyle = (action?: string | null) => {
        // 如果isPickupArrivalScan为false，就展示白色边框
        if (isPickupArrivalScan === false) {
            return 'border-white';
        }

        // 检查是否为ARRIVED_WHS状态，如果是则显示绿色边框
        if (currentOverallStatus === 'ARRIVED_WHS' && (!action|| action === 'None')) {
            return 'border-green-500';
        }
        
        // 检查是否为终态状态，如果是则显示灰色边框
        if (['PRE_DELIVERED' ,'DELIVERED', 'CANCELLED', 'HANDED_OVER', 'RETURNED', 'DESTROYED', 'MISSING', 'DAMAGED'].includes(currentOverallStatus)) {
            return 'border-[#8B8C8F]';
        }

        // 被扫运单状态为 “待派送、派送中” 时
        if (['WAITING_FOR_DELIVERY', 'DELIVERY_IN_PROGRESS'].includes(currentOverallStatus)) {
            return 'border-white';
        }

        if(['CREATED','PICKUP_FAILED','ARRIVED_DESTINATION_HUB','ARRIVED_ORIGIN_HUB','PENDING_SHIPMENT','IN_TRANSIT'].includes(currentOverallStatus)) {
            return 'border-white';
        }

        //被扫运单状态是 “退件开始/退件到达目的地仓” 时
        if (['RETURN_STARTED', 'RETURN_ARRIVED_DESTINATION_HUB'].includes(currentOverallStatus)) {
            return 'border-red-500';
        }

        if (!action) return '';
        
        switch (action.toUpperCase()) {
            case 'HOLD':
                return 'border-blue-600'; // 深一点的亮蓝色 (#2563eb)
            case 'REDELIVER':
                return 'border-green-500'; // 明亮的绿色 (#22c55e)
            case 'RETURN':
                return 'border-red-500'; // 鲜艳红色 (#ef4444)
            case 'RESORT':
            case 'COMPENSATE':
                return 'border-orange-500'; // 明亮橙色 (#f97316)
            default:
                return 'border-white'; // 默认白色边框
        }
    };

    // 根据NextAction获取按钮背景颜色
    const getActionButtonColor = (action?: string | null) => {
        if (!action) return 'bg-white';
        
        switch (action.toUpperCase()) {
            case 'HOLD':
                return 'bg-blue-600'; // 深一点的亮蓝色 (#2563eb)
            case 'REDELIVER':
                return 'bg-green-500'; // 明亮的绿色 (#22c55e)
            case 'RETURN':
                return 'bg-red-500'; // 鲜艳红色 (#ef4444)
            case 'RESORT':
            case 'COMPENSATE':
                return 'bg-orange-500'; // 明亮橙色 (#f97316)
            default:
                return 'bg-white'; // 默认白色背景
        }
    };
    
    const [handleSetNextActionScanEvent] = useMutation(HANDLE_SET_NEXT_ACTION_SCAN_EVENT);

    // 获取下一步动作选项
    const { data: nextActionData, loading: nextActionLoading } = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST, {
        fetchPolicy: 'cache-first'
    });

    // 转换为Picker组件需要的格式，并应用国际化
    const nextActionOptions = React.useMemo(() => {
        if (!nextActionData?.getNextActionList) {
            return [[]];
        }
        
        return [
            nextActionData.getNextActionList
                .filter((action) => action.name !== 'None' && action.name !== 'Compensate') // 过滤掉None和Compensate选项
                .map((action) => ({
                    label: t(`pda.common.exceptionActions.${action.name}`),
                    value: action.name
                }))
        ];
    }, [nextActionData]);

    // 处理下拉框选择并直接提交
    const handleActionSelect = async (value: any[]) => {
        const actionName = value[0] as string;
        if (!actionName) return;

        // 如果选择的动作和当前动作相同，不需要请求
        if (actionName === selectedAction) {
            setPickerVisible(false);
            return;
        }

        const actionLabel = nextActionOptions[0].find((opt: any) => opt.value === actionName)?.label || actionName;

        setIsSubmitting(true);
        setPickerVisible(false);
        try {
            const { data } = await handleSetNextActionScanEvent({
                variables: {
                    barCode: trackingNumber,
                    actionName: actionName
                }
            });

            if (data?.handleSetNextActionScanEvent) {
                // 更新本地状态
                setSelectedAction(actionName);

                Toast.show({
                    content: t('pda.pages.returnToWhs.setNextActionSuccess', {
                        trackingNumber,
                        action: actionLabel
                    }),
                    position: 'center',
                    duration: 2000
                });

                // 通知父组件刷新数据
                if (onNextActionChanged) {
                    onNextActionChanged(actionName, false);
                }
            } else {
                throw new Error('Failed to set next action');
            }
        } catch (error) {
            showError({
                title: t('pda.common.error'),
                message: t('pda.pages.returnToWhs.setNextActionFailed')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // 处理WHS入库扫描
    const handleWhsArrivalScan = async () => {
        setIsSubmitting(true);
        try {
            const { data } = await handleRTWWhsArrivalScanEvent({
                variables: { barCode: trackingNumber }
            });

            if (data?.handleRTWWhsArrivalScanEvent) {
                // 更新本地状态为 ARRIVED_WHS
                setCurrentOverallStatus('ARRIVED_WHS');
                Toast.show({
                    content: t('pda.pages.returnToWhs.whsArrivalScanSuccess'),
                    position: 'center',
                    duration: 2000
                });

                // 通知父组件刷新数据，状态会变为 ARRIVED_WHS
                if (onNextActionChanged) {
                    if (nextAction && nextAction !== 'None') {
                        const reasonCodeToQuery = currentExceptionReasonCode || exceptionReasonCode;
                        if (reasonCodeToQuery === 'ERWHS006') {
                            let defaultNextAction = 'ReDeliver';
                            if(nextAction !== defaultNextAction) {
                                // 4. 设置默认下一步操作
                                await handleSetNextActionScanEvent({
                                    variables: {
                                        barCode: trackingNumber,
                                        actionName: defaultNextAction
                                    }
                                });
                            }
                        }

                        setCurrentIsAbnormal(true);
                        // 如果是ERWHS006异常，使用ReDeliver；否则使用原来的nextAction
                        const finalAction = reasonCodeToQuery === 'ERWHS006' ? 'ReDeliver' : nextAction;
                        setSelectedAction(finalAction); // 更新本地selectedAction状态
                        onNextActionChanged(finalAction, false); // 不关闭弹窗，让用户看到状态变化
                    } else {
                        setCurrentIsAbnormal(false);
                        setSelectedAction(null); // 清空selectedAction，这样getActionColorStyle会显示绿色边框
                        onNextActionChanged('', false); // 不关闭弹窗，让用户看到状态变化
                    }
                }
            } else {
                throw new Error('WHS arrival scan failed');
            }
        } catch (error) {
            showError({
                title: t('pda.common.error'),
                message: t('pda.pages.returnToWhs.whsArrivalScanFailed')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // 处理多图画廊的连拍功能
    const handleMultiPhotoGalleryBurstCapture = async () => {
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);
        const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;

        // 使用PDA专用相机，传递国际化文本
        const photoTitles = [
            t('pda.pages.exception.firstPhoto') || '第一张照片',
            t('pda.pages.exception.secondPhoto') || '第二张照片',
            t('pda.pages.exception.thirdPhoto') || '第三张照片'
        ];

        await pdaOpenCamera(
            remainingPhotoCount, 
            minSelectionCount,
            t('pda.pages.exception.requiredPhotos') || '', // showTips 参数
            photoTitles, // photoTitles 数组
            t('pda.pages.exception.complete') || '', // doneButtonText
            async (photos: string[]) => {
                try {
                    setPhotosArray(prevPhotos => [...prevPhotos, ...photos]);
                } catch (error) {
                    Toast.show({ content: t('pda.pages.exception.multiPhotoProcessingFailed'), position: 'center' });
                }
            }
        );
    };

    // 处理删除照片
    const handleDeletePhoto = (index: number) => {
        setPhotosArray(prevPhotos => prevPhotos.filter((_, i) => i !== index));
    };

    // 处理异常原因确认
    const handleExceptionReasonConfirm = async () => {
        // 检查是否需要上传图片
        const selectedType = getSelectedExceptionType();
        if (selectedType?.isUploadsImages && photosArray.length === 0) {
            Toast.show({
                content: t('pda.pages.exception.pleaseUploadPhotos'),
                position: 'center',
                duration: 2000
            });
            return;
        }

        setIsSubmitting(true);
        try {
            // 准备照片数据 - 上传照片到S3并获取objectKeys
            let exceptionImageS3KeysJson: string[] | null = null;
            
            if (photosArray.length > 0) {
                try {
                    // 将base64照片上传到S3
                    const uploadPromises = photosArray.map(async (base64Photo) => {
                        // 确保base64格式正确
                        const formattedBase64 = base64Photo.startsWith('data:') ? base64Photo : `data:image/jpeg;base64,${base64Photo}`;
                        // 转换为File对象
                        const file = await base64ToFile(formattedBase64, `exception_${Date.now()}.jpg`);
                        // 上传到S3
                        const result = await fileUpload(file, 'exception_photo');
                        return result.objectKey;
                    });
                    
                    exceptionImageS3KeysJson = await Promise.all(uploadPromises);
                } catch (error) {
                    Toast.show({ content: t('pda.pages.exception.photoUploadFailed'), position: 'center' });
                    throw error;
                }
            }

            // 1. 记录异常扫描轨迹
            await handleExceptionScanEvent({
                variables: {
                    trackingNumber: trackingNumber,
                    warehouseSiteType: 'WAREHOUSE',
                    exceptionReasonCode: selectedExceptionReason,
                    exceptionImageS3KeysJson: exceptionImageS3KeysJson
                }
            });

            // 2. 执行返仓扫描，更改状态为暂存
            const { data } = await handleReturnToWhsScanEvent({
                variables: { barCode: trackingNumber, isOperate: true }
            });

            if (data?.result?.success) {
                // 3. 计算默认下一步操作
                let defaultNextAction = data?.result?.nextAction;
                setShowExceptionReasonPicker(false);

                // 更新本地状态以反映后端返回的数据
                setSelectedAction(defaultNextAction);
                setCurrentOverallStatus('ARRIVED_WHS'); // 更新状态为暂存中
                setCurrentIsAbnormal(data.result.isAbnormal || false); // 更新异常状态
                setCurrentExceptionReasonCode(data.result.exceptionReasonCode); // 更新异常原因代码
                setJustReturnedToWhs(true);
                setCurrentExceptionImageS3UrlsJson(data.result.exceptionImageS3UrlsJson || []); // 更新异常图片URLs
                // 通知父组件添加新的成功扫描记录
                if (onNewScanSuccess) {
                    onNewScanSuccess({
                        trackingNumber: trackingNumber,
                        nextAction: defaultNextAction,
                        overallStatus: 'ARRIVED_WHS',
                        exceptionReasonCode: data.result.exceptionReasonCode,
                        isAbnormal: data.result.isAbnormal || false
                    });
                }
                
                // 兼容旧的回调方式
                if (onNextActionChanged) {
                    onNextActionChanged(defaultNextAction, false);
                }
            }
        } catch (error) {
            showError({
                title: t('pda.common.error'),
                message: t('pda.pages.returnToWhs.forceAcceptFailed')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Prevent background scrolling when overlay is shown
    useEffect(() => {
        // Save current scroll position and prevent scrolling
        const originalStyle = window.getComputedStyle(document.body).overflow;
        document.body.style.overflow = 'hidden';
        
        // Cleanup function to restore scrolling when component unmounts
        return () => {
            document.body.style.overflow = originalStyle;
        };
    }, []);

    const handleViewPhotos = async () => {
        // 优先使用 currentExceptionImageS3UrlsJson，如果没有则使用 exceptionImageS3Keys
        const imageData = currentExceptionImageS3UrlsJson && currentExceptionImageS3UrlsJson.length > 0 
            ? currentExceptionImageS3UrlsJson 
            : exceptionImageS3Keys;
            
        if (!imageData || imageData.length === 0) {
            return;
        }

        setIsLoadingImages(true);
        try {
            // 如果是 currentExceptionImageS3UrlsJson，直接使用URL
            // 如果是 exceptionImageS3Keys，需要转换为URL（这里暂时直接使用）
            setImageUrls(imageData);
            setImagesLoaded(true);
        } catch (error) {
            showError({ title: t('pda.common.error'), message: t('pda.pages.returnToWhs.failedToLoadPhotos') });
        } finally {
            setIsLoadingImages(false);
        }
    };

    return (
        <>
            {/* Background overlay to prevent interaction with bottom layer */}
            <div 
                className="fixed inset-0 z-40 bg-black bg-opacity-50"
                onTouchMove={(e) => e.preventDefault()}
                onWheel={(e) => e.preventDefault()}
            />
            
            {/* Full screen overlay */}
            <div className={`fixed inset-0 z-50 bg-white shadow-lg max-h-screen border-8 ${getActionColorStyle(selectedAction)}`}>
                {/* Header with tracking number and close button */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
                    <div className="w-8"></div> {/* 占位元素，保持布局平衡 */}
                    <span className="text-lg font-semibold text-black text-center flex-1">{trackingNumber}</span>
                    <button onClick={onClose} className="p-1 w-8 flex justify-center">
                        <CloseOutline fontSize={24} color="#999" />
                    </button>
                </div>

                {/* Scrollable content area */}
                <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 80px)' }}>

                {/* 被扫运单状态为 "派送失败" 时 */}
                {(['DELIVERY_FAILED'].includes(currentOverallStatus)) && (
                    <div className="text-center py-2 mb-2">
                        <h2 className="text-3xl font-bold text-black mb-2">
                            {t('pda.pages.returnToWhs.returnSuccess')}
                        </h2>
                    </div>
                )}

                {/* 终态状态显示 - 已妥投、已退回、已取消、已交接、已销毁、已丢失、已破损 */}
                {(() => {
                    const finalStatusMap: Record<string, string> = {
                        'PRE_DELIVERED': t('pda.pages.returnToWhs.packagePreDelivered'),
                        'DELIVERED': t('pda.pages.returnToWhs.packageDelivered'),
                        'CANCELLED': t('pda.pages.returnToWhs.packageCancelled'),
                        'HANDED_OVER': t('pda.pages.returnToWhs.packageHandedOver'),
                        'RETURNED': t('pda.pages.returnToWhs.packageReturned'),
                        'DESTROYED': t('pda.pages.returnToWhs.packageDestroyed'),
                        'MISSING': t('pda.pages.returnToWhs.packageMissing'),
                        'DAMAGED': t('pda.pages.returnToWhs.packageDamaged')
                    };
                    const statusText = finalStatusMap[currentOverallStatus];
                    return statusText ? (
                        <div className="text-center py-2 mb-2">
                            <h2 className="text-3xl font-bold text-black mb-2">
                                {statusText}
                            </h2>
                        </div>
                    ) : null;
                })()}

                {/* 被扫运单状态为 "暂存中" 时 */}
                {(['HOLDING'].includes(currentOverallStatus)) && (
                    <div className="text-center py-2 mb-2">
                        <h2 className="text-3xl font-bold text-black mb-2">
                            t('pda.pages.returnToWhs.packageInWhs')
                        </h2>
                    </div>
                )}

                {/* 被扫运单状态是 "退件开始/退件到达目的地仓" 时 */}
                {(['RETURN_STARTED','RETURN_ARRIVED_DESTINATION_HUB'].includes(currentOverallStatus)) && (
                    <div className="text-center py-2 mb-2">
                        <h2 className="text-3xl font-bold text-black mb-2">
                            {t('pda.pages.returnToWhs.packageReturning')}
                        </h2>
                    </div>
                )}

                {/* 被扫运单状态为 "派件仓入库" 时 */}
                {currentOverallStatus === 'ARRIVED_WHS' && (
                    <>
                        {isPickupArrivalScan === true && (
                            <div>
                                {currentIsAbnormal ? (
                                    <div className="text-center py-2 mb-2">
                                        <h2 className="text-3xl font-bold text-black mb-2">
                                            {justReturnedToWhsState
                                                ? t('pda.pages.returnToWhs.returnSuccess')
                                                : t('pda.pages.returnToWhs.packageHasException')
                                            }
                                        </h2>
                                    </div>
                                ) : (
                                    <div className="text-center py-2 mb-2">
                                        <h2 className="text-3xl font-bold text-black mb-2">
                                            {t('pda.pages.returnToWhs.packageArrivedWhs')}
                                        </h2>
                                        <p className="text-base text-black mt-2 font-medium">
                                            {t('pda.pages.returnToWhs.canOperateNormally')}
                                        </p>
                                        <p className="text-base text-gray-600 mt-2">
                                            {t('pda.pages.returnToWhs.arrivedTime')}:{" "}
                                            {destWarehouseArrivedAt
                                                ? formatTimestampToSecondsTime(destWarehouseArrivedAt)
                                                : formatTimestampToSecondsTime(Date.now())}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {isPickupArrivalScan === false && (
                            <div className="text-center py-2 mb-2">
                                <h2 className="text-3xl font-bold text-black mb-2">
                                    {t('pda.pages.returnToWhs.packageMissingPickupArrival')}
                                </h2>
                                <p className="text-base text-black mt-2 font-medium">
                                    {t('pda.pages.returnToWhs.contactHubForProcessing')}
                                </p>
                            </div>
                        )}
                    </>
                )}

                {/* 被扫运单状态为 "已创建、揽收失败、揽收(转运)到达始发(目的地)仓、待发运、运输中" 时 */}
                {(['CREATED','PICKUP_FAILED','ARRIVED_DESTINATION_HUB','ARRIVED_ORIGIN_HUB','PENDING_SHIPMENT','IN_TRANSIT'].includes(currentOverallStatus)) && (
                    <div>
                        {isPickupArrivalScan === false ? (
                            <div className="text-center py-2 mb-2">
                                <h2 className="text-3xl font-bold text-black mb-2">
                                    {t('pda.pages.returnToWhs.packageMissingPickupArrival')}
                                </h2>
                                <p className="text-base text-black mt-2 font-medium">
                                    {t('pda.pages.returnToWhs.contactHubForProcessing')}
                                </p>
                            </div>
                        ) : (
                            <div className="text-center py-2 mb-2">
                                <h2 className="text-3xl font-bold text-black mb-2">
                                    {t('pda.pages.returnToWhs.packageNotWhsArrived')}
                                </h2>
                                {/* WHS入库按钮 */}
                                <button
                                    className="w-full py-3 px-4 mt-4 bg-blue-500 text-white text-lg font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50"
                                    onClick={handleWhsArrivalScan}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? t('pda.common.loading') : t('pda.pages.returnToWhs.whsArrival')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Return Failed Status - 返仓失败状态显示 */}
                {['WAITING_FOR_DELIVERY', 'DELIVERY_IN_PROGRESS'].includes(currentOverallStatus) && (
                    <div className="text-center py-2 mb-2">
                        <h2 className="text-3xl font-bold text-red-600 mb-2">
                            {t('pda.pages.returnToWhs.returnFailed')}
                        </h2>

                        {/* 只在未点击仍要接收时显示当前状态 */}
                        {!showExceptionReasonPicker && (
                            <p className="text-base text-gray-600 mb-4">
                                {t('pda.pages.returnToWhs.packageCurrentStatus', { 
                                    status: getLocalizedStatus(currentOverallStatus) 
                                })}
                            </p>
                        )}

                        {/* 仍要接收按钮 - 点击后变为灰色 */}
                        <button
                            className={`w-full py-3 px-4 text-lg font-medium rounded-lg ${
                                showExceptionReasonPicker
                                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                            } disabled:opacity-50`}
                            onClick={() => setShowExceptionReasonPicker(true)}
                            disabled={showExceptionReasonPicker || isSubmitting}
                        >
                            {t('pda.pages.returnToWhs.stillAccept')}
                        </button>

                        {/* 异常原因选择区域 - 点击仍要接收后显示 */}
                        {showExceptionReasonPicker && (
                            <div className="mt-4">
                                {/* 提示语 - 左对齐 */}
                                <p className="text-base text-gray-600 mb-3 text-left">
                                    {t('pda.pages.returnToWhs.selectExceptionReason')}
                                </p>

                                {/* Exception Reason Dropdown */}
                                <div className="mb-4">
                                    <button
                                        className="w-full py-3 px-4 text-lg border border-gray-300 rounded-lg bg-white flex items-center justify-center relative"
                                        onClick={() => setShowExceptionReasonDropdown(true)}
                                    >
                                                <span className="text-black font-medium">
                                                    {loading ? t('pda.common.loading') :
                                                        selectedExceptionReason ? getExceptionReasonLabel(selectedExceptionReason) :
                                                            t('pda.pages.returnToWhs.selectExceptionReason')}
                                                </span>
                                        <span className="text-gray-400 absolute right-4 text-xl">▼</span>
                                    </button>
                                </div>

                                {/* 照片画廊区域 - 只在需要拍照的异常类型时显示 */}
                                {shouldShowPhotoGallery() && (
                                    <div className="mb-4">
                                        <p className="text-base text-gray-600 mb-3 text-left">
                                            {t('pda.pages.exception.exceptionPhotos')}
                                        </p>
                                        <MultiPhotoGalleryView
                                            photos={photosArray}
                                            onDelete={handleDeletePhoto}
                                            onNativeBurstCapture={handleMultiPhotoGalleryBurstCapture}
                                            maxDisplayCount={kMaxSelectorPhotoLength}
                                        />
                                    </div>
                                )}

                                {/* 确定按钮 */}
                                <button
                                    className="w-full py-3 px-4 bg-blue-500 text-white text-lg font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50"
                                    onClick={handleExceptionReasonConfirm}
                                    disabled={isSubmitting || (shouldShowPhotoGallery() && photosArray.length === 0)}
                                >
                                    {isSubmitting ? t('pda.common.loading') : t('pda.common.confirm')}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Next Action - Combined Display and Selection - 只在isAbnormal为true或操作完成时显示 */}
                {(currentIsAbnormal) && (
                    <div className="py-1 mb-2">
                        <div className="mb-1">
                            <span className="text-gray-600 text-lg font-medium">{t('pda.common.nextAction')}</span>
                        </div>

                        {/* Next Action Dropdown - Shows current and allows selection */}
                        <div className="mb-1">
                            <button
                                className={`w-full py-3 px-4 text-lg border border-gray-300 rounded-lg flex items-center justify-center disabled:opacity-50 relative ${getActionButtonColor(selectedAction)}`}
                                onClick={() => setPickerVisible(true)}
                                disabled={isSubmitting || nextActionLoading}
                            >
                            <span 
                                className={selectedAction ? 'text-white' : 'text-gray-400'}
                                style={{ fontWeight: '600', fontFamily: 'system-ui, -apple-system, sans-serif' }}
                            >
                                {nextActionLoading 
                                    ? t('pda.common.loading')
                                    : selectedAction 
                                        ? nextActionOptions[0].find((opt: any) => opt.value === selectedAction)?.label 
                                        : t('pda.pages.returnToWhs.selectNextAction')
                                }
                            </span>
                                <span className="text-gray-400 absolute right-4 text-xl">▼</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Package Details Section Header */}
                <div className="mb-2">
                    <h3 className="text-lg font-medium text-gray-600">
                        {t('pda.pages.returnToWhs.packageDetails')}
                    </h3>
                </div>

                {/* Order Information - Table with border */}
                <div className="mb-4 border border-gray-300 rounded-lg overflow-hidden bg-white">
                    {/* Tracking Number */}
                    <div className="flex border-b border-gray-200">
                        <div className="w-2/5 px-4 border-r border-gray-200 bg-gray-50">
                            <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.trackingNumber')}</span>
                        </div>
                        <div className="flex-1 px-4 bg-white">
                            <div className="text-base font-medium text-black text-center break-words" style={{ wordBreak: 'break-word' }}>
                                {trackingNumber}
                            </div>
                        </div>
                    </div>

                    {/* Status */}
                    <div className="flex border-b border-gray-200">
                        <div className="w-2/5 px-4 border-r border-gray-200 bg-gray-50">
                            <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.status')}</span>
                        </div>
                        <div className="flex-1 px-4 bg-white">
                            <div className="text-base font-medium text-black text-center break-words" style={{ wordBreak: 'break-word' }}>
                                {getLocalizedStatus(currentOverallStatus)}
                            </div>
                        </div>
                    </div>

                    {/* Reason - 始终显示 */}
                    <div className="flex border-b border-gray-200">
                        <div className="w-2/5 px-4 border-r border-gray-200 bg-gray-50">
                            <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.reason')}</span>
                        </div>
                        <div className="flex-1 px-4 bg-white">
                            <div className="text-base font-medium text-black text-center break-words" style={{ wordBreak: 'break-word' }}>
                                {getLocalizedExceptionReason(exceptionReasonCode || (selectedExceptionReason && justReturnedToWhsState ? selectedExceptionReason : undefined))}
                            </div>
                        </div>
                    </div>

                    {/* Attempts */}
                    {deliveryAttempts !== undefined && (
                        <div className="flex border-b border-gray-200">
                            <div className="w-2/5 px-4 border-r border-gray-200 bg-gray-50">
                                <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.attempts')}</span>
                            </div>
                            <div className="flex-1 px-4 flex items-center justify-center bg-white">
                                <span className="text-base font-medium text-black">{deliveryAttempts}</span>
                            </div>
                        </div>
                    )}

                    {/* Route */}
                    {sortingCode && (
                        <div className="flex border-b border-gray-200">
                            <div className="w-2/5 px-4 border-r border-gray-200 bg-gray-50">
                                <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.route')}</span>
                            </div>
                            <div className="flex-1 px-4 flex items-center justify-center bg-white">
                                <span className="text-base font-medium text-black">{sortingCode}</span>
                            </div>
                        </div>
                    )}

                    {/* Task Date */}
                    <div className="flex border-b border-gray-200">
                        <div className="w-2/5 px-4 border-r border-gray-200 bg-gray-50">
                            <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.taskDate')}</span>
                        </div>
                        <div className="flex-1 px-4 flex items-center justify-center bg-white">
                            <span className="text-base font-medium text-black">{taskDate || '-'}</span>
                        </div>
                    </div>

                    {/* Route Name */}
                    <div className="flex border-b border-gray-200">
                        <div className="w-2/5 px-4 border-r border-gray-200 bg-gray-50">
                            <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.routeName')}</span>
                        </div>
                        <div className="flex-1 px-4 flex items-center justify-center bg-white">
                            <span className="text-base font-medium text-black">{routeName || '-'}</span>
                        </div>
                    </div>

                    {/* Stop Number */}
                    <div className="flex border-b border-gray-200">
                        <div className="w-2/5 px-4 border-r border-gray-200 bg-gray-50">
                            <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.stopNumber')}</span>
                        </div>
                        <div className="flex-1 px-4 flex items-center justify-center bg-white">
                            <span className="text-base font-medium text-black">{stopNumberStr || '-'}</span>
                        </div>
                    </div>

                    {/* Driver Name */}
                    <div className="flex border-b border-gray-200">
                        <div className="w-2/5 px-4 border-r border-gray-200 bg-gray-50">
                            <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.driverName')}</span>
                        </div>
                        <div className="flex-1 px-4 flex items-center justify-center bg-white">
                            <span className="text-base font-medium text-black">{driverAccountName || '-'}</span>
                        </div>
                    </div>

                    {/* DSP Name */}
                    <div className="flex">
                        <div className="w-2/5 px-4 border-r border-gray-200 bg-gray-50">
                            <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.dspName')}</span>
                        </div>
                        <div className="flex-1 px-4 flex items-center justify-center bg-white">
                            <span className="text-base font-medium text-black">{dapName || '-'}</span>
                        </div>
                    </div>
                </div>

                {/* View Photos Button - 只在isAbnormal为true且有图片时显示 */}
                {((currentIsAbnormal || (['RETURN_STARTED','RETURN_ARRIVED_DESTINATION_HUB'].includes(currentOverallStatus))) && ((currentExceptionImageS3UrlsJson && currentExceptionImageS3UrlsJson.length > 0))) && (
                    <div className="pb-4">
                        <button
                            className="w-full py-3 text-center text-base font-medium text-black bg-gray-100 rounded-lg disabled:opacity-50"
                            onClick={handleViewPhotos}
                            disabled={isLoadingImages}
                        >
                            {isLoadingImages ? (
                                <div className="flex items-center justify-center gap-2">
                                    <SpinLoading />
                                    <span>{t('pda.pages.returnToWhs.loadingPhotos')}</span>
                                </div>
                            ) : (
                                t('pda.pages.returnToWhs.viewPhotos')
                            )}
                        </button>
                    </div>
                )}

                {/* Thumbnail previews */}
                {imagesLoaded && imageUrls.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                        {imageUrls.map((url, index) => (
                            <div
                                key={index}
                                className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                                onClick={() => {
                                    setCurrentImageUrl(url);
                                    setImageViewerVisible(true);
                                }}
                            >
                                <img
                                    src={url}
                                    alt={`Exception ${index + 1}`}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const parent = target.parentElement;
                                        if (parent) {
                                            parent.innerHTML = `<div class="w-full h-full flex items-center justify-center text-gray-400 text-sm">${t('pda.pages.returnToWhs.imageLoadFailed')}</div>`;
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
                </div>
            </div>

            {/* Image Viewer */}
            <ImageViewer
                image={currentImageUrl}
                visible={imageViewerVisible}
                onClose={() => setImageViewerVisible(false)}
            />

            {/* Next Action Picker */}
            <Picker
                columns={nextActionOptions}
                visible={pickerVisible}
                onClose={() => setPickerVisible(false)}
                onConfirm={handleActionSelect}
                value={selectedAction ? [selectedAction] : []}
                title={t('pda.pages.returnToWhs.selectNextAction')}
            />

            {/* Exception Reason Picker */}
            <Picker
                visible={showExceptionReasonDropdown && !loading}
                onClose={() => setShowExceptionReasonDropdown(false)}
                value={[selectedExceptionReason]}
                onConfirm={(value) => {
                    const newSelectedReason = value[0] as string;
                    setSelectedExceptionReason(newSelectedReason);
                    setShowExceptionReasonDropdown(false);
                    // 切换异常类型时清空照片
                    setPhotosArray([]);
                }}
                title={t('pda.pages.returnToWhs.selectExceptionReason')}
                columns={[exceptionOptions]}
            />

        </>
    );
});

